import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, Loader2, Lock, LockOpen, Search, Sparkles,
  Download, Trash2, Plus, RotateCcw, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import { ConfidencePill } from "@/components/v3/ScorePill";
import { CompsScatter } from "@/components/v3/PropertyCharts";

/* BP-004 Comparable Sales tab.

   Layout:
     • Top row      — search filters + ARV summary card
     • Middle       — comps table (lock/exclude/adjust)
     • Bottom       — actions (Calculate ARV, Publish, AI Review, PDF)
*/

const DEFAULT_FILTERS = {
  radius_miles: "0.5",
  months_back: 12,
  sqft_tolerance_pct: "0.25",
  beds_tolerance: 1,
  baths_tolerance: "1",
  exclude_distressed: false,
};

const TREND = {
  up: { label: "Trending Up", Icon: TrendingUp, cls: "text-emerald-700 bg-emerald-100" },
  down: { label: "Trending Down", Icon: TrendingDown, cls: "text-red-700 bg-red-100" },
  flat: { label: "Flat", Icon: Minus, cls: "text-slate-700 bg-slate-100" },
  unknown: { label: "—", Icon: Minus, cls: "text-slate-500 bg-slate-100" },
};

export default function ComparableSalesTab({ property }) {
  const [state, setState] = useState({ comps: [], latest_arv: null, published_arv: null });
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showManual, setShowManual] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/comps`);
      setState(data);
    } catch (err) {
      toast.error("Failed to load comps");
    } finally {
      setLoading(false);
    }
  }, [property.id]);

  useEffect(() => { load(); }, [load]);

  const runSearch = useCallback(async () => {
    setSearching(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/comps/search`,
        {
          radius_miles: filters.radius_miles,
          months_back: parseInt(filters.months_back) || 12,
          sqft_tolerance_pct: filters.sqft_tolerance_pct,
          beds_tolerance: parseInt(filters.beds_tolerance) || 0,
          baths_tolerance: filters.baths_tolerance,
          exclude_distressed: !!filters.exclude_distressed,
        },
      );
      toast.success(
        data.comps_added
          ? `Added ${data.comps_added} new comp${data.comps_added === 1 ? "" : "s"}`
          : "No new comps found",
      );
      setState({ ...state, comps: data.comps });
    } catch (err) {
      toast.error("Comp search failed");
    } finally {
      setSearching(false);
    }
  }, [filters, property.id, state]);

  const patchComp = useCallback(async (comp_id, patch) => {
    try {
      await api.patch(`/v3/comps/${comp_id}`, patch);
      await load();
    } catch (err) {
      toast.error("Update failed");
    }
  }, [load]);

  const removeComp = useCallback(async (comp_id) => {
    try {
      await api.delete(`/v3/comps/${comp_id}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  }, [load]);

  const calculate = useCallback(async () => {
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/comps/calculate-arv`,
        { subject_sqft: property.sqft || null },
      );
      setState((s) => ({ ...s, latest_arv: data }));
      toast.success(`ARV v${data.version} calculated`);
    } catch (err) {
      toast.error("Calculation failed");
    } finally {
      setCalculating(false);
    }
  }, [property.id, property.sqft]);

  const publish = useCallback(async () => {
    if (!state.latest_arv) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/v3/comps/arv/${state.latest_arv.id}/publish`);
      setState((s) => ({ ...s, latest_arv: data, published_arv: data }));
      toast.success("ARV published — deal engines updating in background");
    } catch (err) {
      toast.error("Publish failed");
    } finally {
      setPublishing(false);
    }
  }, [state.latest_arv]);

  const runAi = useCallback(async () => {
    if (!state.latest_arv) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/comps/arv/${state.latest_arv.id}/ai-review`);
      setState((s) => ({ ...s, latest_arv: data }));
      toast.success("AI review ready");
    } catch (err) {
      toast.error("AI review failed");
    } finally {
      setAiLoading(false);
    }
  }, [state.latest_arv]);

  const exportPdf = useCallback(() => {
    if (!state.latest_arv) return;
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/comps/arv/${state.latest_arv.id}/export/pdf`,
      "_blank",
    );
  }, [state.latest_arv]);

  const arv = state.latest_arv;
  const includedSet = useMemo(
    () => new Set(arv?.included_comp_ids || []),
    [arv],
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="comps-loading">
        <Loader2 size={14} className="animate-spin"/> Loading comps…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tab-panel-comps">
      {/* Filters */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <F label="Radius (mi)">
            <input type="number" step="0.1" min="0.1" max="10"
                   value={filters.radius_miles}
                   onChange={(e) => setFilters({ ...filters, radius_miles: e.target.value })}
                   data-testid="comps-radius" className="inp w-20"/>
          </F>
          <F label="Months back">
            <input type="number" step="1" min="1" max="60"
                   value={filters.months_back}
                   onChange={(e) => setFilters({ ...filters, months_back: e.target.value })}
                   data-testid="comps-months" className="inp w-20"/>
          </F>
          <F label="Sqft tol.">
            <input type="number" step="0.05" min="0" max="1"
                   value={filters.sqft_tolerance_pct}
                   onChange={(e) => setFilters({ ...filters, sqft_tolerance_pct: e.target.value })}
                   className="inp w-20"/>
          </F>
          <F label="Beds ±">
            <input type="number" step="1" min="0" max="5"
                   value={filters.beds_tolerance}
                   onChange={(e) => setFilters({ ...filters, beds_tolerance: e.target.value })}
                   className="inp w-16"/>
          </F>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 pb-1">
            <input type="checkbox" checked={filters.exclude_distressed}
                   onChange={(e) => setFilters({ ...filters, exclude_distressed: e.target.checked })}/>
            Exclude distressed
          </label>
          <div className="ml-auto flex gap-2">
            <button onClick={runSearch} disabled={searching}
                    data-testid="comps-search-btn"
                    className="inline-flex items-center gap-1 rounded-md bg-amber-500 text-black text-sm font-semibold px-3 py-1.5 hover:bg-amber-400 disabled:opacity-50">
              {searching ? <Loader2 size={13} className="animate-spin"/> : <Search size={13}/>}
              Auto-search comps
            </button>
            <button onClick={() => setShowManual((s) => !s)}
                    data-testid="comps-add-manual-btn"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 text-slate-900 text-sm px-3 py-1.5 hover:border-slate-400">
              <Plus size={13}/> Add manual
            </button>
          </div>
        </div>
        {showManual && (
          <ManualCompForm
            property={property}
            onAdded={() => { setShowManual(false); load(); }}
          />
        )}
      </div>

      {/* ARV summary */}
      <ArvSummaryCard
        arv={arv}
        subjectSqft={property.sqft}
        onCalculate={calculate}
        onPublish={publish}
        onAi={runAi}
        onExport={exportPdf}
        calculating={calculating}
        publishing={publishing}
        aiLoading={aiLoading}
      />

      {/* Comps ARV scatter — visualizes where our ARV sits vs the comp cloud */}
      {state.comps.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 md:p-6" data-testid="comps-scatter-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">ARV vs Comp Cloud</div>
          <div className="text-sm font-semibold text-slate-900 mb-3">
            {arv?.calculated_arv
              ? "Green dashed line = our estimated ARV. Amber line = subject sqft."
              : "Calculate ARV to overlay the estimate on the comp cloud."}
          </div>
          <CompsScatter
            comps={state.comps}
            subjectArv={arv?.calculated_arv}
            subjectSqft={property.sqft}
          />
        </div>
      )}

      {/* Comps table */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100 flex items-center justify-between">
          <span>Comparable sales ({state.comps.length})</span>
          <span className="text-slate-400 normal-case tracking-normal">Adjust · lock · exclude in place</span>
        </div>
        {state.comps.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-600">
            No comps yet. Run <b>Auto-search</b> or click <b>Add manual</b>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="comps-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
                <tr>
                  <th className="text-left p-2">Address</th>
                  <th className="text-left p-2">Sold</th>
                  <th className="text-right p-2">Price</th>
                  <th className="text-right p-2">Sqft</th>
                  <th className="text-right p-2">$/sqft</th>
                  <th className="text-right p-2">Dist.</th>
                  <th className="text-left p-2">Cond.</th>
                  <th className="text-right p-2">Adj.</th>
                  <th className="text-center p-2">In ARV</th>
                  <th className="text-right p-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {state.comps.map((c) => (
                  <CompRow key={c.id} comp={c}
                           included={includedSet.has(c.id)}
                           onPatch={patchComp}
                           onRemove={removeComp}/>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .inp { width:100%; background:#ffffff; border:1px solid #e2e8f0;
               border-radius:6px; padding:5px 8px; font-size:12px; color:#0f172a;
               outline:none; transition:border-color .15s ease, box-shadow .15s ease; }
        .inp:focus { border-color:#f59e0b; box-shadow:0 0 0 3px rgba(245,158,11,0.15); }
      `}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function ArvSummaryCard({ arv, subjectSqft, onCalculate, onPublish, onAi, onExport, calculating, publishing, aiLoading }) {
  const trend = TREND[arv?.market_trend || "unknown"];
  const TrendIcon = trend.Icon;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="comps-arv-card">
      {/* Hero row — Expected ARV is the visual focal point */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Expected ARV</div>
          <div className="text-5xl font-bold text-slate-900 tabular-nums leading-none mt-1" data-testid="comps-arv-expected">
            {arv?.expected_arv ? `$${money(arv.expected_arv)}` : "—"}
          </div>
          <div className="text-[11px] text-slate-600 mt-2">
            {arv ? `ARV version ${arv.version} · ${arv.is_published ? "PUBLISHED" : "draft"}`
                 : "No ARV yet — add comps + click Calculate ARV"}
          </div>
        </div>
        <ConfidencePill score={arv?.confidence_score}/>
      </div>

      {/* Supporting numbers row */}
      <div className="mt-4 grid gap-3 grid-cols-2 sm:grid-cols-4">
        <MetricBox label="Low Estimate" value={arv?.low_arv ? `$${money(arv.low_arv)}` : "—"} testid="comps-arv-low"/>
        <MetricBox label="High Estimate" value={arv?.high_arv ? `$${money(arv.high_arv)}` : "—"} testid="comps-arv-high"/>
        <MetricBox label="Weighted $/sqft" value={arv?.weighted_ppsf ? `$${money(arv.weighted_ppsf, 2)}` : "—"} testid="comps-arv-ppsf"/>
        <MetricBox label="Comps included"
                   value={arv ? `${(arv.included_comp_ids || []).length}` : "0"}
                   testid="comps-arv-count"/>
      </div>

      {/* Reserved for BP-Market Intelligence — placeholders only, no data wiring */}
      <div className="mt-4 border-t border-amber-200 pt-3">
        <div className="text-[10px] uppercase tracking-wider text-amber-800/80 mb-2">
          Market Intelligence <span className="ml-1 text-slate-500 normal-case tracking-normal">(coming soon)</span>
        </div>
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-4">
          <PlaceholderBox
            label="Market Trend"
            testid="market-trend-placeholder"
            fill={arv ? (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${trend.cls}`}>
                <TrendIcon size={11}/> {trend.label}
              </span>
            ) : null}
          />
          <PlaceholderBox label="Neighborhood Score" testid="neighborhood-score-placeholder"/>
          <PlaceholderBox label="Inventory Level" testid="inventory-level-placeholder"/>
          <PlaceholderBox label="Appreciation Trend" testid="appreciation-trend-placeholder"/>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={onCalculate} disabled={calculating}
                data-testid="comps-calculate-btn"
                className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
          {calculating ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
          Calculate ARV
        </button>
        <button onClick={onPublish} disabled={publishing || !arv || arv.is_published}
                data-testid="comps-publish-btn"
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-40">
          {publishing ? <Loader2 size={12} className="animate-spin"/> : <BarChart3 size={12}/>}
          {arv?.is_published ? "Published ✓" : "Publish ARV"}
        </button>
        <button onClick={onAi} disabled={aiLoading || !arv}
                data-testid="comps-ai-btn"
                className="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-purple-50 text-purple-800 text-xs font-semibold px-3 py-1.5 hover:border-purple-500 disabled:opacity-40">
          {aiLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
          AI Comp Review
        </button>
        <button onClick={onExport} disabled={!arv}
                data-testid="comps-pdf-btn"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
          <Download size={12}/> PDF
        </button>
      </div>
      {arv?.warnings?.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="comps-warnings">
          {arv.warnings.map((w, i) => (
            <div key={i} className="text-[11px] text-slate-700">
              <span className="text-[9px] px-1 py-0.5 rounded bg-white text-amber-800 border border-amber-200 mr-2">
                {(w.severity || "info").toUpperCase()}
              </span>
              {w.message}
            </div>
          ))}
        </div>
      )}
      {arv?.ai_review && (
        <div className="mt-3 rounded-lg border border-purple-200 bg-purple-50/50 p-3" data-testid="comps-ai-block">
          <div className="text-[10px] uppercase tracking-wider text-purple-700 mb-1">AI Review</div>
          <div className="text-xs text-slate-900">{arv.ai_review.summary}</div>
          {arv.ai_review.confidenceCommentary && (
            <div className="text-[11px] text-slate-700 mt-1">
              <b>Confidence:</b> {arv.ai_review.confidenceCommentary}
            </div>
          )}
          {arv.ai_review.outliers?.length > 0 && (
            <div className="text-[11px] text-slate-700 mt-1">
              <b>Outliers:</b> {arv.ai_review.outliers.join(", ")}
            </div>
          )}
          <div className="text-[9px] text-slate-500 italic mt-1">{arv.ai_review.disclaimer}</div>
        </div>
      )}
      {!subjectSqft && (
        <div className="mt-2 text-[11px] text-amber-800">
          Subject sqft missing on the property — set it in Property Details for a full ARV range.
        </div>
      )}
    </div>
  );
}

/* Confidence pill — shared primitive lives in `@/components/v3/ScorePill`. */

function MetricBox({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-amber-200/60 bg-white/70 px-3 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-base font-semibold text-slate-900 tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function PlaceholderBox({ label, testid, fill }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white/60 px-3 py-2"
         data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-400 italic mt-0.5">
        {fill || "Coming soon"}
      </div>
    </div>
  );
}

function CompRow({ comp, included, onPatch, onRemove }) {
  const ppsf = useMemo(() => {
    const sale = parseFloat(comp.sale_price || 0);
    const adj = parseFloat(comp.adjustment || 0);
    const sf = parseInt(comp.sqft || 0);
    return sf > 0 ? (sale + adj) / sf : null;
  }, [comp]);
  const [adjInput, setAdjInput] = useState(String(comp.adjustment || "0"));
  const commitAdjustment = () => {
    if (adjInput === String(comp.adjustment || "0")) return;
    onPatch(comp.id, { adjustment: adjInput });
  };
  return (
    <tr className={`border-t border-slate-100 ${comp.excluded ? "opacity-50" : ""}`}
        data-testid={`comps-row-${comp.id.slice(0,8)}`}>
      <td className="p-2 max-w-[220px] truncate" title={comp.address}>
        {comp.address}
        <span className="ml-1 text-[9px] uppercase text-slate-400">{comp.source}</span>
      </td>
      <td className="p-2 text-slate-600">{(comp.sale_date || "").slice(0,10)}</td>
      <td className="p-2 text-right tabular-nums">${money(comp.sale_price)}</td>
      <td className="p-2 text-right tabular-nums">{comp.sqft || "—"}</td>
      <td className="p-2 text-right tabular-nums">{ppsf ? `$${ppsf.toFixed(2)}` : "—"}</td>
      <td className="p-2 text-right tabular-nums">{fmtFloat(comp.distance, 2)}mi</td>
      <td className="p-2">
        <select value={comp.condition || "similar"}
                onChange={(e) => onPatch(comp.id, { condition: e.target.value })}
                className="text-xs bg-white border border-slate-200 rounded px-1 py-0.5">
          <option value="superior">superior</option>
          <option value="similar">similar</option>
          <option value="inferior">inferior</option>
          <option value="renovated">renovated</option>
          <option value="unknown">unknown</option>
        </select>
      </td>
      <td className="p-2 text-right">
        <input type="number" step="500" value={adjInput}
               onChange={(e) => setAdjInput(e.target.value)}
               onBlur={commitAdjustment}
               className="w-20 text-right text-xs bg-white border border-slate-200 rounded px-1 py-0.5"/>
      </td>
      <td className="p-2 text-center">
        <input type="checkbox" checked={!comp.excluded}
               onChange={(e) => onPatch(comp.id, { excluded: !e.target.checked })}
               data-testid={`comps-include-${comp.id.slice(0,8)}`}/>
        {included && <span className="ml-1 text-[9px] text-emerald-700">✓</span>}
      </td>
      <td className="p-2 pr-3 text-right whitespace-nowrap">
        <button onClick={() => onPatch(comp.id, { locked: !comp.locked })}
                data-testid={`comps-lock-${comp.id.slice(0,8)}`}
                className="p-1 text-slate-500 hover:text-slate-800" title={comp.locked ? "Unlock" : "Lock"}>
          {comp.locked ? <Lock size={12}/> : <LockOpen size={12}/>}
        </button>
        <button onClick={() => onRemove(comp.id)}
                data-testid={`comps-delete-${comp.id.slice(0,8)}`}
                className="p-1 text-slate-500 hover:text-red-700 ml-1" title="Remove">
          <Trash2 size={12}/>
        </button>
      </td>
    </tr>
  );
}

function ManualCompForm({ property, onAdded }) {
  const [form, setForm] = useState({
    address: "", sale_price: "", sale_date: "", sqft: "",
    distance: "0.5", beds: "", baths: "", condition: "similar",
    adjustment: "0",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.address || !form.sale_price || !form.sale_date || !form.sqft) {
      toast.error("Address, sale price, sale date, and sqft are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/v3/properties/${property.id}/comps`, {
        address: form.address,
        sale_price: form.sale_price,
        sale_date: form.sale_date,
        sqft: parseInt(form.sqft) || 0,
        distance: form.distance || "0",
        beds: form.beds || null,
        baths: form.baths || null,
        condition: form.condition,
        adjustment: form.adjustment || "0",
      });
      toast.success("Comp added");
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add comp");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="grid gap-2 sm:grid-cols-4 border-t border-slate-100 pt-3" data-testid="comps-manual-form">
      <F label="Address *"><input value={form.address} onChange={set("address")} data-testid="comps-manual-address" className="inp"/></F>
      <F label="Sale price *"><input type="number" min="0" step="1000" value={form.sale_price} onChange={set("sale_price")} data-testid="comps-manual-price" className="inp"/></F>
      <F label="Sale date *"><input type="date" value={form.sale_date} onChange={set("sale_date")} data-testid="comps-manual-date" className="inp"/></F>
      <F label="Sqft *"><input type="number" min="0" step="10" value={form.sqft} onChange={set("sqft")} data-testid="comps-manual-sqft" className="inp"/></F>
      <F label="Distance (mi)"><input type="number" min="0" step="0.05" value={form.distance} onChange={set("distance")} className="inp"/></F>
      <F label="Beds"><input type="number" min="0" step="1" value={form.beds} onChange={set("beds")} className="inp"/></F>
      <F label="Baths"><input type="number" min="0" step="0.5" value={form.baths} onChange={set("baths")} className="inp"/></F>
      <F label="Condition">
        <select value={form.condition} onChange={set("condition")} className="inp">
          <option value="superior">superior</option>
          <option value="similar">similar</option>
          <option value="inferior">inferior</option>
          <option value="renovated">renovated</option>
          <option value="unknown">unknown</option>
        </select>
      </F>
      <div className="sm:col-span-4 flex justify-end">
        <button onClick={submit} disabled={saving}
                data-testid="comps-manual-save"
                className="rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
          {saving ? "Saving…" : "Add comp"}
        </button>
      </div>
    </div>
  );
}

function money(v, digits = 0) {
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(String(v));
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

function fmtFloat(v, digits = 2) {
  if (v === null || v === undefined || v === "") return "0";
  const n = parseFloat(String(v));
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(digits);
}
